import React, { useState, useEffect, useRef, useCallback } from 'react';

const DONOR_COLORS = ['#ff4ecd','#00f5ff','#ffd23f','#b388ff','#56cfb2','#ff7eb3','#7c9eff','#ffaa70'];

const GIFTS = {
  'rose':         { diamonds:1,     icon:'🌹', label:'Rosa' },
  'tiktok':       { diamonds:1,     icon:'🎵', label:'TikTok' },
  'finger heart': { diamonds:5,     icon:'🤙', label:'Finger Heart' },
  'hug':          { diamonds:10,    icon:'🤗', label:'Abrazo' },
  'confetti':     { diamonds:10,    icon:'🎊', label:'Confetti' },
  'rocket':       { diamonds:20,    icon:'🚀', label:'Cohete' },
  'lion':         { diamonds:29,    icon:'🦁', label:'León' },
  'diamond':      { diamonds:5,     icon:'💎', label:'Diamante' },
  'crown':        { diamonds:214,   icon:'👑', label:'Corona' },
  'gg':           { diamonds:199,   icon:'🏆', label:'GG' },
  'train':        { diamonds:888,   icon:'🚂', label:'Tren' },
  'galaxy':       { diamonds:1000,  icon:'🌌', label:'Galaxia' },
  'drama queen':  { diamonds:5000,  icon:'👸', label:'Drama Queen' },
  'interstellar': { diamonds:20000, icon:'🌠', label:'Interestelar' },
  'universe':     { diamonds:34999, icon:'🌍', label:'Universo' },
};

const TIERS = [
  { icon:'🌹', label:'Rosa',     action:'+1 PASO',    color:'#ff7eb3' },
  { icon:'🦁', label:'León',     action:'BOCINA',     color:'#ffd23f' },
  { icon:'🌌', label:'Galaxia',  action:'RETRATO',    color:'#00f5ff' },
  { icon:'💎', label:'Diamante', action:'JEFE FINAL', color:'#b388ff' },
];

function resolveGift(giftName, diamondCount) {
  return GIFTS[(giftName || '').toLowerCase().trim()] ||
    { diamonds: diamondCount || 1, icon: '🎁', label: giftName || 'Regalo' };
}

// ── Avatar ────────────────────────────────────────────────────
function Avatar({ src, nickname, size = 48, style: xs }) {
  const [err, setErr] = useState(false);
  const initial = (nickname || '?')[0].toUpperCase();
  const hash = Array.from(nickname || '').reduce((a, c) => a + c.charCodeAt(0), 0);
  const color = DONOR_COLORS[hash % DONOR_COLORS.length];
  return (src && !err) ? (
    <img src={src} alt={nickname} onError={() => setErr(true)}
      style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover',
        border:`2px solid ${color}`, flexShrink:0, display:'block', ...xs }} />
  ) : (
    <div style={{ width:size, height:size, borderRadius:'50%', background:`${color}22`,
      border:`2px solid ${color}`, display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:Math.round(size*0.38), fontWeight:800, color, fontFamily:'inherit',
      flexShrink:0, ...xs }}>
      {initial}
    </div>
  );
}

// ── Donor Ring ────────────────────────────────────────────────
function DonorRing({ donors, totalCoins, coinsGoal, size = 260, sc = 1 }) {
  const cx = size/2, cy = size/2, R = size/2 - 20, sw = 18;
  const filled = Math.min(1, totalCoins / Math.max(1, coinsGoal));
  const donorArr = Object.values(donors).filter(d => d.coins > 0);
  const GAP_DEG = donorArr.length > 1 ? 1.5 : 0;

  let angle = -90;
  const segs = donorArr.map(d => {
    const sweep = totalCoins > 0 ? Math.max(0, (d.coins / totalCoins) * filled * 360 - GAP_DEG) : 0;
    const a1 = angle * Math.PI / 180;
    const a2 = (angle + sweep) * Math.PI / 180;
    const seg = {
      path: sweep < 0.1 ? '' :
        `M ${cx+R*Math.cos(a1)} ${cy+R*Math.sin(a1)} A ${R} ${R} 0 ${sweep>180?1:0} 1 ${cx+R*Math.cos(a2)} ${cy+R*Math.sin(a2)}`,
      color: d.color, sweep, key: d.uniqueId,
    };
    angle += sweep + GAP_DEG;
    return seg;
  });

  const ticks = Array.from({length:40}, (_,i) => {
    const a = (i/40*360 - 90) * Math.PI / 180;
    const r1 = R + sw/2 + 4, r2 = R + sw/2 + 8;
    return { x1:cx+r1*Math.cos(a), y1:cy+r1*Math.sin(a), x2:cx+r2*Math.cos(a), y2:cy+r2*Math.sin(a) };
  });

  return (
    <div style={{ position:'relative', width:size, height:size, flexShrink:0,
      filter:'drop-shadow(0 0 24px rgba(179,136,255,0.4))' }}>
      <svg width={size} height={size}>
        <defs>
          <filter id="rglow">
            <feGaussianBlur stdDeviation="3" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        {ticks.map((t,i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke="rgba(179,136,255,0.2)" strokeWidth="1"/>
        ))}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={sw}/>
        {segs.map(s => s.path && (
          <path key={s.key} d={s.path} fill="none" stroke={s.color} strokeWidth={sw}
            strokeLinecap="butt" filter="url(#rglow)"
            style={{ transition:'all 0.6s ease' }}/>
        ))}
        <circle cx={cx} cy={cy} r={R - sw/2 - 4}
          fill="rgba(0,0,0,0.45)" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
      </svg>
      <div style={{ position:'absolute', top:'50%', left:'50%',
        transform:'translate(-50%,-50%)', textAlign:'center', pointerEvents:'none' }}>
        <div style={{ fontSize:Math.max(32,Math.round(64*sc)), fontWeight:800, lineHeight:1, letterSpacing:'-2px',
          background:'linear-gradient(135deg,#ffd23f 0%,#ff4ecd 50%,#00f5ff 100%)',
          WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
          filter:'drop-shadow(0 0 16px rgba(255,210,63,0.4))' }}>
          {Math.round(filled*100)}<span style={{fontSize:Math.max(14,Math.round(28*sc)),letterSpacing:0}}>%</span>
        </div>
        <div style={{fontSize:'10px',color:'rgba(179,136,255,0.85)',letterSpacing:'4px',
          fontWeight:600,marginTop:'4px'}}>DEL JEFE FINAL</div>
      </div>
    </div>
  );
}

// ── Jefe Final ────────────────────────────────────────────────
function JefeFinalAnnouncement({ winner, onClose }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 2200);
    const t2 = setTimeout(() => setStage(2), 4500);
    const t3 = setTimeout(onClose, 9500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onClose]);

  const particles = useRef(Array.from({length:32}, (_,i) => ({
    id:i, left:Math.random()*100, delay:Math.random()*1.8,
    color: DONOR_COLORS[i % DONOR_COLORS.length],
    size: 7+Math.random()*11, dur: 3+Math.random()*2.5,
  }))).current;

  return (
    <div style={{ position:'absolute', inset:0, zIndex:100, overflow:'hidden',
      background:'radial-gradient(ellipse at center,#1a0040 0%,#060010 60%,#000 100%)',
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      fontFamily:'"Space Grotesk","Inter",system-ui,sans-serif' }}>
      <div style={{position:'absolute',inset:0,
        background:'radial-gradient(circle at 50% 40%,rgba(255,78,205,0.28),transparent 55%)',
        filter:'blur(40px)'}}/>
      {stage === 0 && (
        <div style={{textAlign:'center',position:'relative',zIndex:2}}>
          <div style={{position:'relative',width:200,height:200,margin:'0 auto'}}>
            {[0,1,2].map(i => (
              <div key={i} style={{position:'absolute',inset:`${i*22}px`,borderRadius:'50%',
                border:'2px solid rgba(255,78,205,0.7)',
                animation:`v4Heartbeat 1.5s ease-out ${i*0.35}s infinite`}}/>
            ))}
            <div style={{position:'absolute',inset:'56px',display:'flex',
              alignItems:'center',justifyContent:'center',fontSize:'52px'}}>🏆</div>
          </div>
          <div style={{fontSize:'48px',fontWeight:900,letterSpacing:'12px',marginTop:'24px',
            background:'linear-gradient(135deg,#ffd23f,#ff4ecd)',
            WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',
            animation:'v4Pulse 1s ease-in-out infinite'}}>JEFE FINAL</div>
        </div>
      )}
      {stage === 1 && (
        <div style={{textAlign:'center',animation:'v4DramaReveal 0.8s ease',position:'relative',zIndex:2}}>
          <div style={{fontSize:'20px',color:'rgba(179,136,255,0.9)',letterSpacing:'6px',
            fontWeight:700,marginBottom:'24px'}}>Y EL GANADOR ES...</div>
          {[0,1,2].map(i => (
            <div key={i} style={{width:14,height:14,borderRadius:'50%',background:DONOR_COLORS[i],
              display:'inline-block',margin:'0 10px',
              animation:`v4DotPulse 0.8s ease ${i*0.2}s infinite`}}/>
          ))}
        </div>
      )}
      {stage === 2 && (
        <div style={{textAlign:'center',position:'relative',zIndex:2}}>
          {particles.map(p => (
            <div key={p.id} style={{position:'absolute',left:`${p.left}%`,top:'-24px',
              width:p.size,height:p.size,borderRadius:'2px',background:p.color,
              animation:`v4fall ${p.dur}s ease ${p.delay}s forwards`}}/>
          ))}
          <div style={{animation:'v4DramaReveal 0.8s ease'}}>
            <Avatar src={winner.profilePictureUrl} nickname={winner.nickname} size={130}
              style={{margin:'0 auto',border:`4px solid ${winner.color}`,
                boxShadow:`0 0 60px ${winner.color}`}}/>
            <div style={{fontSize:'48px',fontWeight:900,marginTop:'16px',
              background:`linear-gradient(135deg,#fff,${winner.color})`,
              WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',
              animation:'v4NamePop 0.7s ease'}}>{winner.nickname}</div>
            <div style={{fontSize:'16px',color:'rgba(179,136,255,0.85)',marginTop:'10px',
              animation:'v4PrizeSlide 0.6s ease 0.3s both'}}>
              🏆 GANÓ UN RETRATO PERSONALIZADO
            </div>
            <div style={{fontSize:'14px',color:winner.color,marginTop:'6px',fontWeight:700}}>
              {winner.coins.toLocaleString()} 💎 donados
            </div>
            <button onClick={onClose} style={{marginTop:'32px',padding:'11px 32px',
              background:'rgba(255,78,205,0.15)',border:'1px solid rgba(255,78,205,0.5)',
              borderRadius:'999px',color:'#ff4ecd',fontSize:'13px',fontWeight:700,
              letterSpacing:'4px',cursor:'pointer',fontFamily:'inherit',
              animation:'v4PrizeSlide 0.6s ease 0.7s both'}}>CONTINUAR</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── WS Badge ──────────────────────────────────────────────────
function WsBadge({ status }) {
  const c = {connected:'#4effa0',connecting:'#ffd23f',disconnected:'#ff5050'}[status]||'#ff5050';
  const l = {connected:'EN VIVO',connecting:'CONECTANDO',disconnected:'OFFLINE'}[status]||'OFFLINE';
  return (
    <div style={{display:'flex',alignItems:'center',gap:'6px',padding:'5px 14px',
      background:`${c}18`,border:`1px solid ${c}40`,borderRadius:'999px',
      fontSize:'12px',fontWeight:700,letterSpacing:'2px',color:c}}>
      <div style={{width:7,height:7,borderRadius:'50%',background:c,
        animation:status==='connected'?'v3pulse 1.5s ease-in-out infinite':'none'}}/>
      {l}
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────
const TikTokLivePanel = ({
  secuenciaSeleccionada, posicionesEjecutadas, posicionActualIndex,
  ejecutando, esperandoAccion, isConnected, onEjecutarPosicion, onVolverMenu, totalPosiciones,
}) => {
  const [bloqueado, setBloqueado]         = useState(false);
  const [lastGift, setLastGift]           = useState(null);
  const [lastGiftPulse, setLastGiftPulse] = useState(0);
  const [wsStatus, setWsStatus]           = useState('disconnected');
  const [showConfig, setShowConfig]       = useState(false);
  const [showSim, setShowSim]             = useState(false);
  const [jefeFinalWinner, setJefeFinalWinner] = useState(null);
  const [portraitQueue, setPortraitQueue] = useState([]);
  const [donors, setDonors]               = useState({});
  const [totalRoundCoins, setTotalRoundCoins] = useState(0);
  const [colaPosiciones, setColaPosiciones]   = useState(0);
  const [coinsGoal, setCoinsGoal]   = useState(() => parseInt(localStorage.getItem('ttCoinsGoal')  || '50000'));
  const [coinsPerPos, setCoinsPerPos] = useState(() => parseFloat(localStorage.getItem('ttCoinsPerPos') || '2.5'));

  const donorsRef      = useRef({});
  const totalCoinsRef  = useRef(0);
  const jefeFiredRef   = useRef(false);
  const bloqueadoRef   = useRef(false);
  const coinsGoalRef   = useRef(50000);
  const coinsPerPosRef = useRef(2.5);
  const procesandoRef  = useRef(false);
  const colaRef        = useRef(0);
  const wsRef          = useRef(null);
  const wsReconnRef    = useRef(null);
  const procesarColaRef = useRef(null);
  const containerRef   = useRef(null);
  const [cw, setCw]    = useState(1100);
  const [ch, setCh]    = useState(650);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect;
      setCw(width); setCh(height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const sc = Math.max(0.42, Math.min(1, cw / 1100, ch / 640));
  const ringSize  = Math.round(260 * sc);
  const haloSz    = Math.round(290 * sc);
  const photoSz   = Math.round(250 * sc);
  const leftW     = Math.round(280 * sc);
  const rightW    = Math.round(300 * sc);
  const heroFont  = Math.max(20, Math.round(48 * sc));
  const gapSz     = Math.max(8,  Math.round(20 * sc));
  const padH      = Math.max(8,  Math.round(16 * sc));
  const padV      = Math.max(8,  Math.round(20 * sc));

  useEffect(() => { bloqueadoRef.current = bloqueado; }, [bloqueado]);
  useEffect(() => { coinsGoalRef.current = coinsGoal; localStorage.setItem('ttCoinsGoal', coinsGoal); }, [coinsGoal]);
  useEffect(() => { coinsPerPosRef.current = coinsPerPos; localStorage.setItem('ttCoinsPerPos', coinsPerPos); }, [coinsPerPos]);

  const procesarCola = useCallback(() => {
    if (procesandoRef.current || colaRef.current <= 0 || bloqueadoRef.current) return;
    if (!isConnected || !secuenciaSeleccionada) return;
    if (totalPosiciones - posicionesEjecutadas.length <= 0) return;
    procesandoRef.current = true;
    colaRef.current -= 1;
    setColaPosiciones(colaRef.current);
    onEjecutarPosicion();
  }, [isConnected, secuenciaSeleccionada, totalPosiciones, posicionesEjecutadas, onEjecutarPosicion]);

  useEffect(() => { procesarColaRef.current = procesarCola; }, [procesarCola]);

  useEffect(() => {
    if (!esperandoAccion && !ejecutando) {
      procesandoRef.current = false;
      if (colaRef.current > 0 && !bloqueadoRef.current) {
        const t = setTimeout(() => procesarColaRef.current?.(), 300);
        return () => clearTimeout(t);
      }
    }
  }, [esperandoAccion, ejecutando]);

  const procesarRegalo = useCallback((data) => {
    const { uniqueId, nickname, profilePictureUrl, giftName, diamondCount, repeatCount = 1 } = data;
    const gift   = resolveGift(giftName, diamondCount);
    const earned = (diamondCount || gift.diamonds) * repeatCount;
    const giftKey = (giftName || '').toLowerCase().trim();

    const updated = { ...donorsRef.current };
    if (!updated[uniqueId]) {
      updated[uniqueId] = {
        uniqueId, nickname, profilePictureUrl, coins: 0, positionsDispatched: 0,
        color: DONOR_COLORS[Object.keys(updated).length % DONOR_COLORS.length],
        joinedAt: Date.now(),
      };
    }
    updated[uniqueId] = { ...updated[uniqueId], nickname, profilePictureUrl,
      coins: updated[uniqueId].coins + earned };

    const newTotal = totalCoinsRef.current + earned;
    const donor    = updated[uniqueId];
    const shouldHave = Math.floor(donor.coins / coinsPerPosRef.current);
    const newPos  = Math.max(0, shouldHave - donor.positionsDispatched);
    if (newPos > 0 && !bloqueadoRef.current) {
      updated[uniqueId].positionsDispatched += newPos;
      colaRef.current += newPos;
      setColaPosiciones(colaRef.current);
      if (!procesandoRef.current) setTimeout(() => procesarColaRef.current?.(), 100);
    }

    donorsRef.current   = updated;
    totalCoinsRef.current = newTotal;
    setDonors({ ...updated });
    setTotalRoundCoins(newTotal);

    // Galaxia → auto retrato
    if (giftKey === 'galaxy') {
      setPortraitQueue(prev => [...prev, {
        uniqueId, nickname, profilePictureUrl, color: donor.color,
        hora: new Date().toLocaleTimeString('es-MX', { hour:'2-digit', minute:'2-digit' }),
      }]);
    }

    if (!jefeFiredRef.current && newTotal >= coinsGoalRef.current) {
      jefeFiredRef.current = true;
      const top = Object.values(updated).reduce((a, b) => a.coins > b.coins ? a : b);
      setJefeFinalWinner(top);
    }

    setLastGift({ nickname, profilePictureUrl, icon: gift.icon, label: gift.label,
      earned, newPos, donorColor: donor.color, giftKey });
    setLastGiftPulse(p => p + 1);
  }, []);

  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    setWsStatus('connecting');
    const ws = new WebSocket('ws://localhost:8080');
    wsRef.current = ws;
    ws.onopen  = () => setWsStatus('connected');
    ws.onclose = () => { setWsStatus('disconnected'); wsReconnRef.current = setTimeout(connectWs, 5000); };
    ws.onerror = () => ws.close();
    ws.onmessage = e => {
      try { const d = JSON.parse(e.data); if (d.type === 'gift') procesarRegalo(d); } catch {}
    };
  }, [procesarRegalo]);

  useEffect(() => {
    connectWs();
    return () => { clearTimeout(wsReconnRef.current); wsRef.current?.close(); };
  }, [connectWs]);

  useEffect(() => {
    window.__tiktokLive = {
      recibirRegalo: (giftName, repeatCount = 1, nickname = 'Test') => {
        const g = resolveGift(giftName, null);
        procesarRegalo({ uniqueId:`sim_${nickname}`, nickname, profilePictureUrl:null,
          giftName, diamondCount:g.diamonds, repeatCount });
      },
      getEstado: () => ({ bloqueado:bloqueadoRef.current, cola:colaRef.current,
        totalCoins:totalCoinsRef.current }),
    };
    return () => { delete window.__tiktokLive; };
  }, [procesarRegalo]);

  const handleJefeFinalClose = useCallback(() => {
    const w = jefeFinalWinner;
    setJefeFinalWinner(null);
    if (w) setPortraitQueue(prev => [...prev,
      { uniqueId:w.uniqueId, nickname:w.nickname, profilePictureUrl:w.profilePictureUrl,
        color:w.color, hora: new Date().toLocaleTimeString('es-MX', { hour:'2-digit', minute:'2-digit' }) }]);
    donorsRef.current = {}; totalCoinsRef.current = 0; jefeFiredRef.current = false;
    setDonors({}); setTotalRoundCoins(0);
  }, [jefeFinalWinner]);

  const resetRound = () => {
    donorsRef.current = {}; totalCoinsRef.current = 0; jefeFiredRef.current = false;
    setDonors({}); setTotalRoundCoins(0);
  };

  const donorList = Object.values(donors).sort((a, b) => b.coins - a.coins);
  const topDonor  = donorList[0] || null;
  const progreso  = totalPosiciones > 0
    ? Math.round((posicionesEjecutadas.length / totalPosiciones) * 100) : 0;
  const pasoActual = posicionesEjecutadas.length;

  return (
    <div ref={containerRef} style={S.root}>
      {jefeFinalWinner && <JefeFinalAnnouncement winner={jefeFinalWinner} onClose={handleJefeFinalClose}/>}
      <div style={S.a1}/><div style={S.a2}/><div style={S.a3}/>
      <div style={S.dotgrid}/>

      <div style={{...S.layout, padding:`${padH}px ${padV}px ${Math.max(6,Math.round(12*sc))}px`, gap:Math.max(6,Math.round(10*sc))}}>


        {/* ── HEADER ─────────────────────────────────────── */}
        <div style={S.header}>
          <div style={S.hLeft}>
            {!ejecutando && (
              <button onClick={onVolverMenu} style={S.backBtn}>← Volver</button>
            )}
            <span style={{color:'#ff4ecd',filter:'drop-shadow(0 0 8px #ff4ecd)',fontSize:'10px'}}>●</span>
            <span style={S.brandMain}>ROCHARAMAS</span>
            <span style={{color:'rgba(179,136,255,0.4)',fontSize:'18px',fontWeight:300}}>/</span>
            <span style={S.brandSub}>LIVE STRING ART</span>
          </div>

          <div style={S.tiers}>
            {TIERS.map(t => (
              <div key={t.label} style={{...S.tier, borderColor:`${t.color}40`}}>
                <span style={{fontSize:'14px'}}>{t.icon}</span>
                <span style={{color:'rgba(255,255,255,0.8)',fontWeight:600,fontSize:'11px'}}>{t.label}</span>
                <span style={{color:'rgba(179,136,255,0.5)',fontSize:'10px'}}>→</span>
                <span style={{color:t.color,fontWeight:700,fontSize:'11px',letterSpacing:'1px'}}>
                  {t.action}
                </span>
              </div>
            ))}
          </div>

          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <WsBadge status={wsStatus}/>
            <button onClick={() => setShowConfig(v=>!v)} style={S.cfgBtn}>
              ⚙
            </button>
            <button
              onClick={() => setBloqueado(v => { bloqueadoRef.current=!v; return !v; })}
              style={{...S.lockBtn,
                background:bloqueado?'rgba(255,80,80,0.15)':'rgba(0,245,255,0.08)',
                borderColor:bloqueado?'rgba(255,80,80,0.6)':'rgba(0,245,255,0.4)',
                color:bloqueado?'#ff5050':'#00f5ff',
              }}>
              {bloqueado ? '🔒 BLOQUEADO' : '🔓 ACTIVO'}
            </button>
          </div>
        </div>

        {/* Config panel */}
        {showConfig && (
          <div style={S.cfgPanel}>
            <div style={S.cfgRow}>
              <span style={S.cfgLbl}>Meta Jefe Final (💎)</span>
              <input type="number" value={coinsGoal} min="100"
                onChange={e => setCoinsGoal(Math.max(100, parseInt(e.target.value)||100))}
                style={S.cfgInput}/>
            </div>
            <div style={S.cfgRow}>
              <span style={S.cfgLbl}>💎 por posición</span>
              <input type="number" value={coinsPerPos} min="0.1" step="0.5"
                onChange={e => setCoinsPerPos(Math.max(0.1, parseFloat(e.target.value)||0.1))}
                style={S.cfgInput}/>
            </div>
            <button onClick={resetRound} style={{...S.cfgLbl,cursor:'pointer',background:'none',
              border:'none',color:'rgba(255,80,80,0.7)',fontFamily:'inherit'}}>
              🔄 Reiniciar ronda
            </button>
          </div>
        )}

        {/* ── MAIN 3-COL ─────────────────────────────────── */}
        <div style={{...S.main, gridTemplateColumns:`${leftW}px 1fr ${rightW}px`, gap:gapSz}}>

          {/* LEFT: Ring + winner card + paso stats */}
          <div style={S.leftCol}>
            <DonorRing donors={donors} totalCoins={totalRoundCoins} coinsGoal={coinsGoal} size={ringSize} sc={sc}/>

            {/* Ganador actual */}
            {topDonor ? (
              <div style={S.winnerCard}>
                <div style={S.winnerStripe}/>
                <div style={{fontSize:'10px',color:'#ffd23f',letterSpacing:'3px',
                  fontWeight:700,marginBottom:'10px'}}>GANADOR ACTUAL</div>
                <div style={{display:'grid',gridTemplateColumns:'auto 1fr auto',
                  gap:'10px',alignItems:'center'}}>
                  <Avatar src={topDonor.profilePictureUrl} nickname={topDonor.nickname}
                    size={44} style={{border:`2px solid ${topDonor.color}`}}/>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:'15px',fontWeight:700,color:'#fff',
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {topDonor.nickname}
                    </div>
                    <div style={{fontSize:'12px',color:'#ffd23f',fontWeight:600,marginTop:'2px'}}>
                      {topDonor.coins.toLocaleString()} 💎 ·{' '}
                      {totalRoundCoins > 0 ? Math.round(topDonor.coins/totalRoundCoins*100) : 0}%
                    </div>
                  </div>
                  <span style={{fontSize:'22px',filter:'drop-shadow(0 0 6px rgba(255,210,63,0.7))'}}>
                    🏆
                  </span>
                </div>
              </div>
            ) : (
              <div style={{...S.winnerCard,opacity:0.4}}>
                <div style={S.winnerStripe}/>
                <div style={{fontSize:'10px',color:'#ffd23f',letterSpacing:'3px',
                  fontWeight:700,marginBottom:'8px'}}>GANADOR ACTUAL</div>
                <div style={{fontSize:'12px',color:'rgba(255,255,255,0.5)',textAlign:'center',
                  padding:'8px 0'}}>esperando donantes...</div>
              </div>
            )}

            {/* Paso stats */}
            <div style={S.pasoCard}>
              <div style={S.pasoHalf}>
                <div style={{fontSize:'9px',color:'rgba(179,136,255,0.7)',letterSpacing:'3px',
                  fontWeight:700,marginBottom:'4px'}}>PASO</div>
                <div style={{display:'flex',alignItems:'baseline',gap:'3px'}}>
                  <span style={{fontSize:'28px',fontWeight:800,color:'#fff',lineHeight:1}}>
                    {pasoActual}
                  </span>
                  <span style={{fontSize:'13px',color:'rgba(179,136,255,0.6)',fontWeight:500}}>
                    /{totalPosiciones}
                  </span>
                </div>
                <div style={{marginTop:'6px',height:'3px',borderRadius:'999px',
                  background:'rgba(179,136,255,0.2)',overflow:'hidden'}}>
                  <div style={{height:'100%',borderRadius:'999px',transition:'width 0.5s ease',
                    width:`${progreso}%`,
                    background:'linear-gradient(90deg,#ff4ecd,#00f5ff)'}}/>
                </div>
              </div>
              <div style={{width:'1px',background:'rgba(179,136,255,0.2)'}}/>
              <div style={S.pasoHalf}>
                <div style={{fontSize:'9px',color:'rgba(179,136,255,0.7)',letterSpacing:'3px',
                  fontWeight:700,marginBottom:'4px'}}>EN COLA</div>
                <div style={{display:'flex',alignItems:'baseline',gap:'3px'}}>
                  <span style={{fontSize:'28px',fontWeight:800,
                    color:colaPosiciones>0?'#ff4ecd':'rgba(179,136,255,0.4)',lineHeight:1}}>
                    {colaPosiciones}
                  </span>
                </div>
                {procesandoRef.current && (
                  <div style={{marginTop:'6px',display:'flex',alignItems:'center',gap:'4px',
                    fontSize:'9px',color:'#ff4ecd'}}>
                    <div style={{width:'5px',height:'5px',borderRadius:'50%',background:'#ff4ecd',
                      animation:'v3pulse 1s infinite'}}/>
                    ejecutando
                  </div>
                )}
              </div>
            </div>

            {/* Donors list */}
            {donorList.length > 1 && (
              <div style={{width:'100%',display:'flex',flexDirection:'column',gap:'4px'}}>
                {donorList.slice(1,5).map((d,i) => (
                  <div key={d.uniqueId} style={{display:'flex',alignItems:'center',gap:'8px',
                    padding:'5px 8px',background:'rgba(255,255,255,0.03)',
                    border:'1px solid rgba(255,255,255,0.07)',borderRadius:'10px'}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:d.color,
                      boxShadow:`0 0 6px ${d.color}`,flexShrink:0}}/>
                    <span style={{fontSize:'12px',color:'rgba(179,136,255,0.7)',fontWeight:700,width:'16px'}}>
                      {i+2}
                    </span>
                    <Avatar src={d.profilePictureUrl} nickname={d.nickname} size={24}/>
                    <span style={{flex:1,fontSize:'12px',color:'#fff',
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {d.nickname}
                    </span>
                    <span style={{fontSize:'11px',color:'#00f5ff',fontWeight:700}}>
                      {d.coins.toLocaleString()}💎
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* CENTER: Hero last gift */}
          <div style={S.centerCol}>
            {lastGift ? (
              <div key={lastGiftPulse} style={S.heroWrap}>
                <div style={S.heroBadge}>
                  <span style={S.heroDot}/>
                  ÚLTIMO REGALO
                </div>

                {/* Halo + photo */}
                <div style={{...S.haloWrap, width:haloSz, height:haloSz}}>
                  <div style={S.haloRing1}/>
                  <div style={S.haloRing2}/>
                  <div style={{...S.heroPhotoFrame, width:photoSz, height:photoSz,
                    border:`3px solid ${lastGift.donorColor}`,
                    boxShadow:`0 0 50px ${lastGift.donorColor}80`}}>
                    <Avatar src={lastGift.profilePictureUrl} nickname={lastGift.nickname}
                      size={photoSz - 4}
                      style={{border:'none',borderRadius:'50%',
                        boxShadow:'none',animation:'v4pop 0.6s ease'}}/>
                  </div>
                  {/* Gift bubble */}
                  <div style={{...S.giftBubble,
                    background: lastGift.donorColor,
                    boxShadow:`0 0 20px ${lastGift.donorColor}cc`}}>
                    <span style={{fontSize:'18px'}}>{lastGift.icon}</span>
                  </div>
                </div>

                <div style={{...S.heroNickname, fontSize:heroFont}}>{lastGift.nickname}</div>

                <div style={S.heroGiftLine}>
                  <span style={{fontSize:'26px'}}>{lastGift.icon}</span>
                  <span style={{color:'#fff',fontWeight:600,fontSize:'18px'}}>{lastGift.label}</span>
                  <span style={{color:'rgba(179,136,255,0.4)'}}>·</span>
                  <span style={{color:'#00f5ff',fontWeight:700,fontSize:'18px',
                    textShadow:'0 0 10px rgba(0,245,255,0.6)'}}>
                    +{lastGift.earned.toLocaleString()} 💎
                  </span>
                </div>

                <div style={S.heroMeta}>
                  {lastGift.giftKey === 'galaxy' && (
                    <div style={{...S.heroChip,
                      background:'rgba(0,245,255,0.12)',border:'1px solid rgba(0,245,255,0.4)',
                      color:'#7fdfff',fontWeight:700,letterSpacing:'1px'}}>
                      🖼️ RETRATO ACTIVADO
                    </div>
                  )}
                  {lastGift.newPos > 0 && (
                    <div style={{...S.heroChip,
                      background:`${lastGift.donorColor}18`,
                      border:`1px solid ${lastGift.donorColor}50`,
                      color:lastGift.donorColor,fontWeight:700}}>
                      ► paso {pasoActual} activado
                    </div>
                  )}
                  <div style={S.heroChip}>
                    {secuenciaSeleccionada?.nombre || 'secuencia'}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',
                gap:'12px',opacity:0.5,textAlign:'center'}}>
                <div style={{fontSize:'64px',opacity:0.3}}>🎁</div>
                <div style={{fontSize:'20px',fontWeight:800,letterSpacing:'6px',
                  color:'rgba(179,136,255,0.6)'}}>ESPERANDO REGALOS</div>
                <div style={{fontSize:'13px',color:'rgba(179,136,255,0.4)',letterSpacing:'2px'}}>
                  los regalos de TikTok moverán la máquina
                </div>
                {wsStatus==='disconnected' && (
                  <div style={{marginTop:'8px',padding:'10px 18px',
                    background:'rgba(255,80,80,0.08)',border:'1px solid rgba(255,80,80,0.3)',
                    borderRadius:'12px',fontSize:'12px',color:'rgba(255,80,80,0.7)',
                    lineHeight:1.8}}>
                    Servidor desconectado.<br/>
                    <code style={{fontFamily:'monospace',fontSize:'11px'}}>node tiktokServer.js</code>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Ranking */}
          <div style={S.rightCol}>
            <div style={{fontSize:'13px',color:'rgba(179,136,255,0.8)',fontWeight:700,
              letterSpacing:'3px',marginBottom:'4px'}}>RANKING</div>
            <div style={{fontSize:'10px',color:'rgba(179,136,255,0.5)',letterSpacing:'2px',
              marginBottom:'14px'}}>aportación al jefe final</div>

            {/* #1 big card */}
            {topDonor ? (
              <div style={S.kingCard}>
                <div style={{position:'absolute',top:0,left:0,right:0,bottom:0,
                  background:`radial-gradient(circle at 80% 20%, ${topDonor.color}20, transparent 60%)`,
                  borderRadius:'18px',pointerEvents:'none'}}/>
                <div style={{position:'absolute',top:'12px',right:'12px',
                  fontSize:'10px',fontWeight:700,letterSpacing:'2px',color:'#ffe600',
                  background:'rgba(255,230,0,0.12)',border:'1px solid rgba(255,230,0,0.5)',
                  borderRadius:'999px',padding:'4px 10px'}}>🏆 TOP DONANTE</div>
                <div style={{display:'flex',alignItems:'center',gap:'14px',position:'relative'}}>
                  <div style={{fontSize:'34px',fontWeight:800,lineHeight:1,
                    background:'linear-gradient(135deg,#ff4ecd,#b388ff)',
                    WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>#1</div>
                  <Avatar src={topDonor.profilePictureUrl} nickname={topDonor.nickname}
                    size={56} style={{border:`3px solid ${topDonor.color}`,
                      boxShadow:`0 0 16px ${topDonor.color}80`}}/>
                </div>
                <div style={{fontSize:'20px',fontWeight:700,color:'#fff',marginTop:'10px',
                  position:'relative',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {topDonor.nickname}
                </div>
                <div style={{fontSize:'11px',color:'rgba(179,136,255,0.6)',
                  letterSpacing:'2px',marginTop:'2px'}}>
                  @{topDonor.nickname.toLowerCase().replace(/\s/g,'_')}
                </div>
                <div style={{marginTop:'8px',position:'relative'}}>
                  <span style={{fontSize:'36px',fontWeight:800,lineHeight:1,
                    background:'linear-gradient(135deg,#00f5ff,#b388ff)',
                    WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
                    {topDonor.coins.toLocaleString()}
                  </span>
                  <span style={{fontSize:'13px',color:'rgba(179,136,255,0.6)',marginLeft:'6px'}}>
                    monedas
                  </span>
                </div>
              </div>
            ) : (
              <div style={{...S.kingCard,opacity:0.3}}>
                <div style={{textAlign:'center',padding:'20px 0',fontSize:'13px',
                  color:'rgba(179,136,255,0.5)'}}>sin donantes aún</div>
              </div>
            )}

            {/* List #2-6 */}
            <div style={{display:'flex',flexDirection:'column',gap:'6px',flex:1,overflowY:'auto'}}>
              {donorList.slice(1,7).map((d,i) => (
                <div key={d.uniqueId} style={{display:'grid',
                  gridTemplateColumns:'8px 20px 30px 1fr auto',
                  gap:'8px',alignItems:'center',padding:'7px 12px',
                  background:'rgba(255,255,255,0.03)',
                  border:'1px solid rgba(255,255,255,0.08)',borderRadius:'10px'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:d.color,
                    boxShadow:`0 0 6px ${d.color}`}}/>
                  <span style={{fontSize:'13px',fontWeight:700,color:'#b388ff'}}>{i+2}</span>
                  <Avatar src={d.profilePictureUrl} nickname={d.nickname} size={26}/>
                  <span style={{fontSize:'13px',color:'#fff',fontWeight:500,
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {d.nickname}
                  </span>
                  <span style={{fontSize:'12px',color:'#00f5ff',fontWeight:700,whiteSpace:'nowrap'}}>
                    {d.coins.toLocaleString()} 💎
                  </span>
                </div>
              ))}
              {donorList.length === 0 && (
                <div style={{textAlign:'center',padding:'20px',fontSize:'12px',
                  color:'rgba(179,136,255,0.4)'}}>
                  esperando donantes...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── PRÓXIMAS PIEZAS ────────────────────────────── */}
        <div style={S.queueSection}>
          <div style={S.queueHead}>
            <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
              <span style={{fontSize:'28px',filter:'drop-shadow(0 0 12px #00f5ff)'}}>🖼️</span>
              <div>
                <div style={{fontSize:'14px',fontWeight:700,letterSpacing:'4px',color:'#fff'}}>
                  PRÓXIMAS PIEZAS DE ARTE
                </div>
                <div style={{fontSize:'11px',color:'rgba(179,136,255,0.7)',marginTop:'2px'}}>
                  quien manda Galaxia · obtiene un retrato físico hecho en hilo
                </div>
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
              {portraitQueue.length > 0 && (
                <button onClick={() => setPortraitQueue([])} style={{background:'none',border:'none',
                  color:'rgba(179,136,255,0.4)',fontSize:'10px',cursor:'pointer',fontFamily:'inherit'}}>
                  limpiar
                </button>
              )}
              <div style={{display:'flex',alignItems:'baseline',gap:'6px',
                background:'rgba(0,245,255,0.1)',border:'1px solid rgba(0,245,255,0.4)',
                borderRadius:'999px',padding:'6px 14px'}}>
                <span style={{fontSize:'20px',fontWeight:800,color:'#00f5ff'}}>
                  {portraitQueue.length}
                </span>
                <span style={{fontSize:'11px',color:'rgba(0,245,255,0.7)',letterSpacing:'2px'}}>
                  en cola
                </span>
              </div>
              <button onClick={() => setShowSim(v=>!v)} style={{...S.lockBtn,
                background:'rgba(255,78,205,0.08)',borderColor:'rgba(255,78,205,0.3)',
                color:'rgba(255,78,205,0.8)',padding:'6px 14px'}}>
                SIMULADOR {showSim?'▼':'▶'}
              </button>
            </div>
          </div>

          <div style={{display:'flex',gap:'12px',overflowX:'auto',paddingBottom:'2px'}}>
            {portraitQueue.length === 0 && [0,1,2,3].map(i => (
              <div key={i} style={{...S.qCard,opacity:0.3,minWidth:'180px',flexShrink:0}}>
                <div style={{fontSize:'10px',color:'rgba(179,136,255,0.5)',fontWeight:700,
                  letterSpacing:'2px',marginBottom:'8px'}}>0{i+1}</div>
                <div style={{width:44,height:44,borderRadius:'50%',
                  background:'rgba(179,136,255,0.1)',border:'2px dashed rgba(179,136,255,0.3)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:'18px',opacity:0.4}}>?</div>
                <div style={{fontSize:'12px',color:'rgba(179,136,255,0.4)',
                  marginTop:'8px',fontStyle:'italic'}}>tu lugar aquí</div>
                <div style={{fontSize:'10px',color:'rgba(179,136,255,0.3)',marginTop:'4px'}}>
                  envía Galaxia 🌌
                </div>
              </div>
            ))}
            {portraitQueue.map((p, i) => (
              <div key={`${p.uniqueId}_${i}`}
                style={{...S.qCard, minWidth:'180px', flexShrink:0,
                  ...(i===0 ? S.qCardNext : {})}}>
                {i === 0 && (
                  <div style={{position:'absolute',top:'-10px',left:'14px',
                    background:'#00f5ff',color:'#0a0014',fontSize:'10px',fontWeight:800,
                    letterSpacing:'3px',padding:'3px 10px',borderRadius:'999px',
                    boxShadow:'0 0 12px rgba(0,245,255,0.7)'}}>SIGUIENTE</div>
                )}
                <div style={{fontSize:'10px',color:'rgba(179,136,255,0.6)',fontWeight:700,
                  letterSpacing:'2px',marginBottom:'8px'}}>0{i+1}</div>
                <Avatar src={p.profilePictureUrl} nickname={p.nickname} size={48}
                  style={{border:`2px solid ${i===0?'#fff':p.color}`,
                    boxShadow:i===0?`0 0 16px rgba(0,245,255,0.7)`:`0 0 8px ${p.color}50`}}/>
                <div style={{fontSize:'13px',fontWeight:700,color:'#fff',
                  marginTop:'8px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {p.nickname}
                </div>
                <div style={{fontSize:'10px',color:'rgba(0,245,255,0.8)',marginTop:'4px',
                  fontWeight:700}}>🌌 ×1</div>
                <div style={{fontSize:'10px',color:'rgba(255,255,255,0.3)',marginTop:'auto',
                  paddingTop:'6px'}}>{p.hora}</div>
              </div>
            ))}

            {/* Simulador panel inline */}
            {showSim && (
              <div style={{...S.qCard,minWidth:'240px',flexShrink:0,
                background:'rgba(255,78,205,0.06)',border:'1px solid rgba(255,78,205,0.3)'}}>
                <div style={{fontSize:'10px',color:'rgba(255,78,205,0.8)',letterSpacing:'3px',
                  fontWeight:700,marginBottom:'8px'}}>SIMULADOR</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'5px'}}>
                  {Object.entries(GIFTS).slice(0,8).map(([key,g]) => (
                    <button key={key} disabled={bloqueado}
                      style={{display:'flex',alignItems:'center',gap:'5px',
                        background:'rgba(255,255,255,0.04)',
                        border:'1px solid rgba(179,136,255,0.2)',
                        borderRadius:'8px',padding:'5px 7px',fontFamily:'inherit',
                        cursor:bloqueado?'not-allowed':'pointer',opacity:bloqueado?0.3:1}}
                      onClick={() => procesarRegalo({
                        uniqueId:'sim01',nickname:'Simulador',profilePictureUrl:null,
                        giftName:key,diamondCount:g.diamonds,repeatCount:1,
                      })}>
                      <span style={{fontSize:'14px'}}>{g.icon}</span>
                      <div style={{textAlign:'left'}}>
                        <div style={{fontSize:'9px',fontWeight:700,color:'#fff'}}>{g.label}</div>
                        <div style={{fontSize:'8px',color:'#ffd23f'}}>{g.diamonds}💎</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────
const S = {
  root:{ position:'relative',width:'100%',height:'100%',overflow:'hidden',
    background:'radial-gradient(ellipse at 30% 40%,#1a0033 0%,#0a0014 60%,#000 100%)',
    fontFamily:'"Space Grotesk","Inter",system-ui,sans-serif',color:'#fff' },
  a1:{ position:'absolute',top:'-20%',left:'20%',width:'60%',height:'80%',
    background:'radial-gradient(circle,rgba(255,78,205,0.25),transparent 60%)',filter:'blur(60px)',pointerEvents:'none' },
  a2:{ position:'absolute',bottom:'-30%',right:'-10%',width:'70%',height:'80%',
    background:'radial-gradient(circle,rgba(0,245,255,0.18),transparent 60%)',filter:'blur(80px)',pointerEvents:'none' },
  a3:{ position:'absolute',top:'40%',left:'-10%',width:'40%',height:'60%',
    background:'radial-gradient(circle,rgba(179,136,255,0.18),transparent 60%)',filter:'blur(60px)',pointerEvents:'none' },
  dotgrid:{ position:'absolute',inset:0,pointerEvents:'none',
    backgroundImage:'radial-gradient(rgba(179,136,255,0.18) 1px,transparent 1px)',
    backgroundSize:'24px 24px',opacity:0.4 },

  layout:{ position:'relative',zIndex:1,display:'grid',height:'100%',overflow:'hidden',
    gridTemplateRows:'auto auto 1fr auto',padding:'16px 20px 12px',gap:'10px' },

  // Header
  header:{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexShrink:0 },
  hLeft:{ display:'flex',alignItems:'center',gap:'10px' },
  backBtn:{ background:'rgba(255,255,255,0.05)',border:'1px solid rgba(179,136,255,0.25)',
    borderRadius:'8px',padding:'5px 12px',color:'rgba(179,136,255,0.8)',fontSize:'11px',
    fontWeight:700,letterSpacing:'2px',cursor:'pointer',fontFamily:'inherit' },
  brandMain:{ fontSize:'20px',fontWeight:800,letterSpacing:'3px' },
  brandSub:{ fontSize:'12px',color:'rgba(179,136,255,0.7)',letterSpacing:'3px',fontWeight:500 },
  tiers:{ display:'flex',gap:'6px',flexWrap:'wrap' },
  tier:{ display:'flex',alignItems:'center',gap:'6px',background:'rgba(255,255,255,0.03)',
    border:'1px solid',borderRadius:'999px',padding:'5px 12px',
    backdropFilter:'blur(10px)' },
  lockBtn:{ display:'flex',alignItems:'center',gap:'6px',border:'1px solid',borderRadius:'999px',
    padding:'5px 14px',fontSize:'11px',fontWeight:800,letterSpacing:'2px',
    cursor:'pointer',fontFamily:'inherit',transition:'all 0.3s ease' },
  cfgBtn:{ background:'rgba(255,255,255,0.05)',border:'1px solid rgba(179,136,255,0.25)',
    borderRadius:'8px',padding:'6px 10px',color:'rgba(179,136,255,0.7)',fontSize:'14px',
    cursor:'pointer',fontFamily:'inherit' },

  // Config
  cfgPanel:{ display:'flex',alignItems:'center',gap:'16px',padding:'10px 14px',
    background:'rgba(0,0,0,0.4)',border:'1px solid rgba(179,136,255,0.2)',
    borderRadius:'12px',flexWrap:'wrap',flexShrink:0 },
  cfgRow:{ display:'flex',alignItems:'center',gap:'8px' },
  cfgLbl:{ fontSize:'11px',color:'rgba(179,136,255,0.7)',fontWeight:600 },
  cfgInput:{ width:'80px',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(179,136,255,0.3)',
    borderRadius:'6px',padding:'4px 8px',color:'#fff',fontSize:'12px',
    fontFamily:'monospace',textAlign:'center' },

  // Main grid
  main:{ display:'grid',gridTemplateColumns:'280px 1fr 300px',gap:'20px',
    minHeight:0,overflow:'hidden' },

  // Left col
  leftCol:{ display:'flex',flexDirection:'column',alignItems:'center',gap:'10px',
    overflowY:'auto',paddingBottom:'4px' },
  winnerCard:{ position:'relative',width:'100%',
    background:'rgba(255,210,63,0.06)',border:'1px solid rgba(255,210,63,0.35)',
    borderRadius:'14px',padding:'12px 14px',backdropFilter:'blur(20px)',overflow:'hidden' },
  winnerStripe:{ position:'absolute',top:0,left:0,right:0,height:'2px',
    background:'linear-gradient(90deg,#ffd23f,#ff4ecd,#00f5ff)',
    boxShadow:'0 0 8px rgba(255,210,63,0.6)' },
  pasoCard:{ width:'100%',display:'grid',gridTemplateColumns:'1fr auto 1fr',gap:'0',
    background:'rgba(255,255,255,0.03)',border:'1px solid rgba(179,136,255,0.2)',
    borderRadius:'14px',padding:'12px 16px',backdropFilter:'blur(20px)' },
  pasoHalf:{ display:'flex',flexDirection:'column' },

  // Center col
  centerCol:{ display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden' },
  heroWrap:{ display:'flex',flexDirection:'column',alignItems:'center',gap:'8px',
    animation:'v4pop 0.5s ease' },
  heroBadge:{ display:'flex',alignItems:'center',gap:'8px',
    background:'rgba(255,78,205,0.15)',border:'1px solid rgba(255,78,205,0.5)',
    borderRadius:'999px',padding:'6px 18px',
    fontSize:'12px',color:'#ffb3e6',letterSpacing:'3px',fontWeight:600 },
  heroDot:{ width:'6px',height:'6px',borderRadius:'50%',background:'#ff4ecd',
    boxShadow:'0 0 8px #ff4ecd',display:'inline-block',
    animation:'v3pulse 1.2s ease-in-out infinite' },
  haloWrap:{ position:'relative',width:'290px',height:'290px',
    display:'flex',alignItems:'center',justifyContent:'center' },
  haloRing1:{ position:'absolute',inset:0,borderRadius:'50%',
    background:'conic-gradient(from 0deg,#ff4ecd,#b388ff,#00f5ff,#ff4ecd)',
    filter:'blur(24px)',opacity:0.65,animation:'v3spin 8s linear infinite' },
  haloRing2:{ position:'absolute',inset:'24px',borderRadius:'50%',
    border:'2px solid rgba(255,255,255,0.35)',
    boxShadow:'0 0 28px rgba(255,78,205,0.5),inset 0 0 28px rgba(0,245,255,0.25)' },
  heroPhotoFrame:{ position:'relative',width:'250px',height:'250px',borderRadius:'50%',
    overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center' },
  giftBubble:{ position:'absolute',bottom:'8px',right:'8px',
    borderRadius:'999px',padding:'8px 14px',
    display:'flex',alignItems:'center',gap:'4px',
    border:'3px solid #fff',zIndex:2 },
  heroNickname:{ fontSize:'48px',fontWeight:800,lineHeight:1,letterSpacing:'0.5px',
    background:'linear-gradient(135deg,#fff 0%,#ffb3e6 100%)',
    WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',
    filter:'drop-shadow(0 0 24px rgba(255,78,205,0.5))',
    marginTop:'4px',textAlign:'center',
    maxWidth:'500px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' },
  heroGiftLine:{ display:'flex',alignItems:'center',gap:'10px',marginTop:'2px' },
  heroMeta:{ display:'flex',gap:'8px',flexWrap:'wrap',justifyContent:'center',marginTop:'4px' },
  heroChip:{ background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.18)',
    borderRadius:'999px',padding:'5px 12px',fontSize:'11px',color:'rgba(255,255,255,0.85)',
    fontWeight:500,backdropFilter:'blur(10px)' },

  // Right col
  rightCol:{ display:'flex',flexDirection:'column',gap:'10px',overflow:'hidden' },
  kingCard:{ position:'relative',
    background:'linear-gradient(135deg,rgba(255,78,205,0.18) 0%,rgba(179,136,255,0.18) 100%)',
    border:'1.5px solid rgba(255,78,205,0.5)',borderRadius:'18px',
    padding:'16px 18px',overflow:'hidden',backdropFilter:'blur(20px)',flexShrink:0 },

  // Queue section
  queueSection:{ background:'linear-gradient(135deg,rgba(0,245,255,0.06),rgba(179,136,255,0.06))',
    border:'1px solid rgba(0,245,255,0.3)',borderRadius:'16px',
    padding:'12px 16px',flexShrink:0,display:'flex',flexDirection:'column',gap:'10px' },
  queueHead:{ display:'flex',justifyContent:'space-between',alignItems:'center' },
  qCard:{ position:'relative',flex:'0 0 auto',minWidth:'160px',
    background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.12)',
    borderRadius:'14px',padding:'12px 14px',display:'flex',flexDirection:'column',gap:'0' },
  qCardNext:{ background:'linear-gradient(135deg,rgba(0,245,255,0.15),rgba(179,136,255,0.15))',
    border:'1.5px solid #00f5ff',
    boxShadow:'0 0 20px rgba(0,245,255,0.3),inset 0 0 12px rgba(0,245,255,0.08)' },
};

export default TikTokLivePanel;
